/**
 * Capture and normalize drawing-board SVG for thumbnails and saved quotes.
 * Strips foreignObject HTML (editable dimension inputs) — invalid in standalone SVG/XML.
 */

import { stripSvgDimensionLabels } from './drawingDimensionOverlays';

const SVG_NS = 'http://www.w3.org/2000/svg';

const stripForeignObjectsRaw = (svgHtml) =>
  svgHtml.replace(/<foreignObject\b[^>]*>[\s\S]*?<\/foreignObject>/gi, '');

const stripMmUnit = (label) => String(label).replace(/\s*mm\s*$/i, '').trim();

const extractForeignObjectLabel = (fo) => {
  const input = fo.querySelector('input');
  if (input) {
    const value = input.value ?? input.getAttribute('value') ?? '';
    return stripMmUnit(String(value));
  }

  const spans = Array.from(fo.querySelectorAll('span'))
    .map((span) => span.textContent?.trim())
    .filter(Boolean)
    .filter((text) => text.toLowerCase() !== 'mm');
  if (spans.length) return stripMmUnit(spans.join(' '));

  return stripMmUnit(fo.textContent?.trim() || '');
};

const readInlineStyle = (el, prop, fallback) => {
  const style = el?.getAttribute?.('style') || el?.style?.cssText || '';
  const match = style.match(new RegExp(`${prop}\\s*:\\s*([^;]+)`, 'i'));
  return match?.[1]?.trim() || fallback;
};

const isPerPartSegmentLabel = (fo) => {
  const w = parseFloat(fo.getAttribute('width') || 0);
  return w > 0 && w <= 55;
};

const isDimensionLabelText = (textEl) => {
  if (textEl.getAttribute('data-dimension-label') === 'true') return true;
  const content = textEl.textContent?.trim() || '';
  if (!content || content.length === 1) return false;
  return /\d/.test(content);
};

const resolveEmbedFontSize = (rawSize, labelCount) => {
  const parsed = parseFloat(rawSize) || 12;
  const corrected = parsed > 20 ? 14 : parsed;
  let size = Math.min(16, Math.max(11, corrected));

  if (labelCount > 4) size = Math.min(size, 11);
  else if (labelCount > 2) size = Math.min(size, 12);

  return Math.round(size);
};

const styleDimensionLabel = (textEl, fontSize) => {
  textEl.setAttribute('font-size', String(fontSize));
  textEl.setAttribute('font-weight', '600');
  textEl.setAttribute('fill', '#2C2C2C');
  textEl.removeAttribute('stroke');
  textEl.removeAttribute('stroke-width');
  textEl.removeAttribute('paint-order');
  textEl.setAttribute('data-dimension-label', 'true');
};

const cleanupLegacyLabelBackgrounds = (svg) => {
  svg.querySelectorAll('rect[data-dimension-label-bg="true"]').forEach((rect) => rect.remove());
};

const enhanceDimensionLabelsForEmbed = (svg) => {
  if (!svg) return;

  cleanupLegacyLabelBackgrounds(svg);

  const dimensionTexts = Array.from(svg.querySelectorAll('text')).filter(isDimensionLabelText);
  const labelCount = dimensionTexts.length;
  const embedFontSize = resolveEmbedFontSize(14, labelCount);

  dimensionTexts.forEach((textEl) => {
    const stripped = stripMmUnit(textEl.textContent?.trim() || '');
    if (stripped) textEl.textContent = stripped;

    const currentSize = textEl.getAttribute('font-size') || '12';
    styleDimensionLabel(textEl, resolveEmbedFontSize(currentSize, labelCount) || embedFontSize);
  });
};

export const replaceForeignObjectsWithText = (svgRoot) => {
  if (!svgRoot) return;

  const foreignObjects = Array.from(svgRoot.querySelectorAll('foreignObject'));
  const mainLabels = foreignObjects.filter((fo) => !isPerPartSegmentLabel(fo));
  const embedFontSize = resolveEmbedFontSize(14, mainLabels.length);

  foreignObjects.forEach((fo) => {
    if (isPerPartSegmentLabel(fo)) {
      fo.remove();
      return;
    }

    const label = extractForeignObjectLabel(fo);
    const x = parseFloat(fo.getAttribute('x') || 0);
    const y = parseFloat(fo.getAttribute('y') || 0);
    const w = parseFloat(fo.getAttribute('width') || 0);
    const h = parseFloat(fo.getAttribute('height') || 0);

    if (label) {
      const input = fo.querySelector('input');
      const rawFontSize = parseInt(readInlineStyle(input, 'font-size', '12'), 10) || 12;
      const fontSize = resolveEmbedFontSize(rawFontSize, mainLabels.length);
      const fill = readInlineStyle(input, 'color', '#2C2C2C');
      const inputAlign = readInlineStyle(input, 'text-align', 'center');
      const anchor = inputAlign === 'left' ? 'start' : 'middle';
      const textX = anchor === 'start' ? x + 4 : x + w / 2;
      const textY = y + h / 2;

      const textEl = document.createElementNS(SVG_NS, 'text');
      textEl.setAttribute('x', String(textX));
      textEl.setAttribute('y', String(textY));
      textEl.setAttribute('text-anchor', anchor);
      textEl.setAttribute('dominant-baseline', 'middle');
      textEl.setAttribute('fill', fill);
      textEl.textContent = label;
      styleDimensionLabel(textEl, fontSize);
      fo.replaceWith(textEl);
      return;
    }

    fo.remove();
  });
};

const GRAY_CANVAS_FILL = /^#f5f5f5$/i;

const applyCanvasBackground = (svg, color) => {
  if (!svg) return;
  svg.querySelectorAll('rect').forEach((rect) => {
    const fill = rect.getAttribute('fill');
    if (fill && GRAY_CANVAS_FILL.test(fill)) {
      rect.setAttribute('fill', color);
    }
  });
};

const normalizeSvgElement = (svg, { saved = false } = {}) => {
  if (!svg) return null;

  replaceForeignObjectsWithText(svg);
  enhanceDimensionLabelsForEmbed(svg);
  applyCanvasBackground(svg, saved ? '#FFFFFF' : 'none');
  svg.removeAttribute('style');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

  if (!svg.getAttribute('xmlns')) {
    svg.setAttribute('xmlns', SVG_NS);
  }

  return svg;
};

const serializeSvg = (svg) => new XMLSerializer().serializeToString(svg);

const XLINK_NS = 'http://www.w3.org/1999/xlink';

const resolveSvgImageUrl = (href) => {
  if (!href || href.startsWith('data:')) return null;
  if (href.startsWith('http://') || href.startsWith('https://')) return href;
  const base = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
  if (href.startsWith('/')) return `${base}${href}`;
  return `${base}/${href}`;
};

/** Inline external SVG <image> hrefs as data URLs (required for iOS Safari + PDF export). */
export const inlineSvgExternalImages = async (svgHtml) => {
  if (!svgHtml || typeof document === 'undefined') return svgHtml;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgHtml, 'image/svg+xml');
    const svg = doc.querySelector('svg');
    if (!svg) return svgHtml;

    const images = Array.from(svg.querySelectorAll('image'));
    const cache = {};

    await Promise.all(
      images.map(async (img) => {
        const href = img.getAttribute('href') || img.getAttributeNS(XLINK_NS, 'href');
        const fullUrl = resolveSvgImageUrl(href);
        if (!fullUrl) return;

        if (!cache[fullUrl]) {
          try {
            const resp = await fetch(fullUrl);
            const blob = await resp.blob();
            cache[fullUrl] = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
          } catch {
            return;
          }
        }

        img.setAttribute('href', cache[fullUrl]);
        try {
          img.setAttributeNS(XLINK_NS, 'href', cache[fullUrl]);
        } catch {
          // ignore xlink set failures
        }
      })
    );

    return serializeSvg(svg);
  } catch {
    return svgHtml;
  }
};

export const stripDimensionLabelsFromSvgHtml = (svgHtml) => {
  if (!svgHtml || typeof document === 'undefined') return svgHtml;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgHtml, 'image/svg+xml');
    const svg = doc.querySelector('svg');
    if (!svg) return svgHtml;

    stripSvgDimensionLabels(svg);
    return serializeSvg(svg);
  } catch {
    return svgHtml;
  }
};

export const prepareSvgHtmlForDisplay = async (
  svgHtml,
  { saved = true, stripDimensionLabels = false } = {}
) => {
  let prepared = prepareSvgHtmlForEmbed(svgHtml, { saved });
  if (!prepared) return null;

  if (stripDimensionLabels) {
    prepared = stripDimensionLabelsFromSvgHtml(prepared);
  }

  return inlineSvgExternalImages(prepared);
};

const applyBackgroundToSvgHtml = (svgHtml, saved) =>
  svgHtml.replace(/\bfill="#[fF]5[fF]5[fF]5"/g, saved ? 'fill="#FFFFFF"' : 'fill="none"');

export const prepareSvgHtmlForEmbed = (svgHtml, { saved = false } = {}) => {
  if (!svgHtml || typeof svgHtml !== 'string') return null;

  try {
    const parser = new DOMParser();
    let doc = parser.parseFromString(svgHtml, 'image/svg+xml');
    let parseError = doc.querySelector('parsererror');
    let svg = doc.querySelector('svg');

    if (!svg || parseError) {
      const cleaned = stripForeignObjectsRaw(svgHtml);
      doc = parser.parseFromString(cleaned, 'image/svg+xml');
      parseError = doc.querySelector('parsererror');
      svg = doc.querySelector('svg');
    }

    if (svg) {
      return serializeSvg(normalizeSvgElement(svg, { saved }));
    }
  } catch {
    // fall through to regex cleanup
  }

  return applyBackgroundToSvgHtml(
    stripForeignObjectsRaw(svgHtml)
      .replace(/\sstyle="[^"]*"/gi, '')
      .replace(/<svg\b/i, '<svg xmlns="http://www.w3.org/2000/svg"')
      .replace(/\swidth="[^"]*"/i, ' width="100%"')
      .replace(/\sheight="[^"]*"/i, ' height="100%"'),
    saved
  );
};

export const svgHtmlToDataUrl = (svgHtml, options = {}) => {
  const prepared = options.alreadyPrepared
    ? svgHtml
    : prepareSvgHtmlForEmbed(svgHtml, options);
  if (!prepared) return null;

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(prepared)}`;
};

const findMainSvg = (root) => {
  if (!root) return null;
  if (root.tagName === 'svg') return root;

  const svgs = Array.from(root.querySelectorAll('svg'));
  if (!svgs.length) return null;

  let best = svgs[0];
  let bestScore = 0;

  svgs.forEach((svg) => {
    const rect = svg.getBoundingClientRect();
    const score = rect.width * rect.height;
    const viewBox = svg.getAttribute('viewBox');
    const viewScore = viewBox
      ? viewBox.split(/\s+/).slice(2).reduce((a, b) => a * parseFloat(b || 0), 1)
      : 0;
    const finalScore = score > 0 ? score : viewScore;
    if (finalScore > bestScore) {
      bestScore = finalScore;
      best = svg;
    }
  });

  return best;
};

const syncForeignObjectInputValues = (sourceSvg, targetSvg) => {
  const sourceObjects = sourceSvg.querySelectorAll('foreignObject');
  const targetObjects = targetSvg.querySelectorAll('foreignObject');
  sourceObjects.forEach((sourceFo, index) => {
    const targetFo = targetObjects[index];
    if (!targetFo) return;
    const sourceInput = sourceFo.querySelector('input');
    const targetInput = targetFo.querySelector('input');
    if (sourceInput && targetInput) {
      targetInput.value = sourceInput.value;
    }
  });
};

export const captureDrawingSvg = (drawingBoardRef, { saved = false } = {}) => {
  const root = drawingBoardRef?.current;
  const svgElement = findMainSvg(root);
  if (!svgElement) return null;

  const clone = svgElement.cloneNode(true);
  syncForeignObjectInputValues(svgElement, clone);
  const rawHtml = new XMLSerializer().serializeToString(clone);
  return prepareSvgHtmlForEmbed(rawHtml, { saved });
};
